<template>
  <form
    class="sm-form sm-form__regist"
    @submit.prevent
  >
    <div
      v-if="activeTab == 0" 
      class="sm-form__regist-tab"
    >
      <input-text
        v-model="formData.user_name"
        :class="'username'"
        :label="`Username`" 
        :name="`user_name`"
        :autocomplete="`username`"
        v-validate="`required|min:6|alpha_num`"
        data-vv-as="Username"
        :error="errors.first('user_name')"
        :showLoader="isValidating.user_name"
        @blur="validateDB('user_name')"
      />
      <input-text
        v-model="formData.email"
        :label="`Email`"
        :name="'email'"
        :autocomplete="`email`"
        v-validate="`required|email`"
        data-vv-as="Email"
        :error="errors.first('email')"
        :showLoader="isValidating.email"
        :isEmail="true"
        @blur="validateDB('email')"
      />
      <input-text
        v-model="formData.additional_email"
        :label="`Additional email`"
        :name="'additional_email'"
        :autocomplete="`email`"
        v-validate="`email`"
        data-vv-as="Additional email"
        :error="errors.first('additional_email') 
            || (formData.email.toLowerCase().trim() 
                === formData.additional_email.toLowerCase().trim() 
            && formData.additional_email.toLowerCase().trim() !== '' 
            && 'Email and Additional email addresses cannot be the same!')"
        :isEmail="true"
      />
      <div class="sm-form__regist-flex">
        <input-text
          v-model="formData.first_name"
          :label="`First name`"
          :name="'first_name'"
          :autocomplete="`given-name`"
          v-validate="'required|first_name_validator|min:2|max:100'"
          data-vv-as="First name"
          :error="errors.first('first_name')"
        />
        <input-text
          v-model="formData.last_name"
          :label="`Last name`" 
          :name="'last_name'"
          :autocomplete="`family-name`"
          v-validate="'required|last_name_validator|min:1|max:100'"
          data-vv-as="Last name"
          :error="errors.first('last_name')"
        />
      </div>
      <input-text
        v-model="formData.company_name"
        :label="`Company`" 
        :name="'company_name'"
        :autocomplete="`organization`"
      />
      <input-text
        v-model="formData.password"
        :label="`Password`" 
        :name="'password'"
        :type="'password'"
        :autocomplete="`new-password`"
        v-validate="{
          required: $route.query.first_sso == 'true' && formPrefillData.email === formData.email ? false : true,
          min: 8,
          regex: passwordRegex
        }"
        data-vv-as="Password"
        :error="errors.first('password')"
      />
      
      <btn-block
        :text="'Continue'"
        type="button"
        @click="validateFirstPart"
      />
      <div v-show="showOfferings" class="sm-form__offerings">
        <router-link :to="{ path: '/get_voffice' }" class="sm-link sm-link-blue"><include-svg :name="'icon-back'"/> Back to Offerings</router-link>
      </div>
    </div>
    <div
      v-if="activeTab == 1" 
      class="sm-form__regist-tab"
    >
      <div class="sm-form__regist-block"
        :class="{'error': errors.first('phone_number')}"
      >
        <phone-input
          v-model="formData.phone"
          :start_val="formData.phone"
          :label="'Phone'"
          :show_errors="true"
          :error_text="errors.first('phone_number') || ''"
          class="sm-form__regist-label"
        />
      </div>

      <country-dropdown
        v-model="selectedCountry"
        :class="'sm-form__regist-label'"
        :countryList="countryList"
        :startVal="selectedCountry"
        :error="errors.first('country_id')"
      />

      <div class="sm-form__regist-flex">
        <input-text 
          v-model="formData.street"
          :label="`Street`" 
          :name="'street'"
          v-validate="`required`"
          :autocomplete="`street-address`"
          :error="errors.first('street')"
        />
        <input-text 
          v-model="formData.city"
          :label="`City`" 
          :name="'city'"
          v-validate="`required`"
          :autocomplete="`address-level2`"
          :error="errors.first('city')"
        />
      </div>

      <div 
        class="sm-form__regist-flex"
        :class="{
          'no-state': !stateList.length
        }"
      >
        <state-dropdown
          v-if="stateList.length > 0"
          v-model="selectedState"
          :class="'sm-form__regist-label'"
          :stateList="stateList"
          :startVal="selectedState"
          :error="errors.first('state_id')"
        />

        <input-zip
          v-model="formData.zip_code"
          v-validate="'required|is_zip'"
          :country="selectedCountry"
          :error="errors.first('zip_code')"
        />
      </div>

      <div class="sm-form__regist-check">
        <checkbox-block
          v-model="acceptAll"
          :name="`accept_all`"
          data-vv-as="Accept Terms of Service and Privacy Policy"
          v-validate="`required:true`"
        />
        <span>I accept the </span>
        <a 
          :href="links.terms"
          target="_blank"
        >Terms of Service</a>
        <span> and </span>
        <a
          :href="links.policy"
          target="_blank"
        >Privacy Policy</a>
        
        <span 
          class="sm-input-text-error"
          v-if="errors.first('accept_all')"
        >
          {{ errors.first('accept_all') }}
        </span>
      </div>

      <div 
        v-if="provider.name && (provider.terms_of_service_url || provider.terms_of_service_external_url)"
        class="sm-form__regist-check"
      >
        <checkbox-block
          v-model="acceptCustomTerms"
          :name="`accept_custom_terms`"
          :data-vv-as="`Accept ${toUpper(provider.name)} Terms of Service`"
          v-validate="`required:true`"
        />
        <span>I accept <b class="sm-txt-strong">{{toUpper(provider.name)}}</b>&nbsp;</span>
        <a 
          :href="provider.terms_of_service_url || provider.terms_of_service_external_url"
          target="_blank"
        >Terms of Service</a>

        <span 
          class="sm-input-text-error"
          v-if="errors.first('accept_custom_terms')"
        >
          {{ errors.first('accept_custom_terms') }}
        </span>
      </div>
      <btn-block
        :text="'Continue'"
        type="button"
        @click="onVerify"
      />
      <div v-show="showOfferings" class="sm-form__offerings">
        <router-link :to="{ path: '/get_voffice' }" class="sm-link sm-link-blue"><include-svg :name="'icon-back'"/> Back to Offerings</router-link>
      </div>
    </div>
    <div
      v-if="activeTab == 2" 
      class="sm-form__regist-tab"
    >
      <h4>Verify your Email</h4>
      <div class="verif-msg">
        <p >We sent a verification code to <span class="verif-email">{{formData.email}}</span></p>
        <p>Please enter the verification code below to verify your account.</p>
      </div>
      <input-text 
        v-model.trim="formData.verification_code"
        :label="``" 
        :name="'verification_code'"
        :error="''"
      />
      <btn-block
        :text="'Create account'"
        :type="'submit'"
        @click="handleSubmit"
        :classes="[{'disabled':formData.verification_code.length < 8}]"
      />
      <btn-block
        :text="'Resend the verification code'"
        :type="'grey'"
        @click="onVerify"
      />
    </div>
    <sm-spinner :show="showSpinner" />
  </form>
</template>

<script>
import InputText from '@/components/InputText';
import InputZip from '@/components/InputZip';
import PhoneInput from '@/components/PhoneInput';
import Checkbox from '@/components/Checkboxes';
import CountryDropdown from '@/components/CountryDropdown';
import StateDropdown from '@/components/StateDropdown';
import FormDropdowns from '@/mixins/FormDropdowns';
import Urls from '@/settings/urls';
import { mapGetters, mapActions } from 'vuex';
import GeneralSettings from '@/settings';
import {ToUpper} from '@/utils/general-data';

export default {
  name: 'form-regist',
  components: {
    'input-text': InputText,
    'input-zip': InputZip,
    'phone-input': PhoneInput,
    'checkbox-block': Checkbox,
    'country-dropdown': CountryDropdown,
    'state-dropdown': StateDropdown,
  },
  props: {
    activeTab: {
      type: Number,
      default: 0
    },
    backErrors: {
      type: Array,
      default: () => []
    },
    formPrefillData: {
      type: Object,
      default: () => new Object()
    },
    showOfferings: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      formData: {
        // First tab
        user_name: '',
        email: '',
        additional_email: '',
        first_name:'',
        last_name: '',
        company_name:'',
        password: '',
        // Second tab
        phone: '',
        country_id: '',
        state_id: '',
        street: '',
        city: '',
        zip_code: '',
        accept_terms: false,
        accept_policy: false,
        verification_code: '',
      },
      acceptAll: false,
      acceptCustomTerms: false,
      links: {
        terms: Urls.links.terms,
        policy: Urls.links.policy,
      },
      isValidating: {
        user_name: false,
        email: false
      },
      passwordRegex: GeneralSettings.passwordRegex,
      firstTabKeys: [
        'user_name',
        'email',
        // 'additional_email',
        'first_name',
        'last_name',
        'company_name',
        'password'
      ],
      showSpinner: false,
    };
  },
  computed: {
    ...mapGetters({
      countryList: 'countryList',
      provider: 'provider'
    }),
  },
  created() {
    this.mixinUserAccount = this.provider;

    this.updateFormData();
  },
  mixins: [FormDropdowns],
  methods: {
    ...mapActions({
      validateAccount: 'account/validateAccount',
      updateUser: 'account/updateUser',
    }),
    toUpper: ToUpper,
    updateFormData() {
      if (Object.keys(this.formPrefillData).length)
      Object.keys(this.formPrefillData).forEach(key => {
        if (this.formData.hasOwnProperty(key))
          this.formData[key] = this.formPrefillData[key];
      }); 
    },
    validateFirstPart() {

      if (this.errors.items.length || this.isValidating.user_name || this.isValidating.email)
        return;

      this.$validator.validate()
        .then(valid => {
          if(!valid) 
            return;

          const main_email = this.formData.email.toLowerCase().trim();
          const add_email = this.formData.additional_email.toLowerCase().trim();
          if( main_email === add_email && add_email !== '') {
            this.errors.add({
              field: "additional_email",
              msg: 'Email and Additional email addresses cannot be the same!'
            })
            return;
          }

          this.$emit('tabUpdate', 1);
        });
    },
    validateSecondPart(cb) {
      this.$validator.validate().then(valid => {
        if (!this.formData.phone) {
          this.errors.add({
            field: 'phone_number',
            msg: 'Enter phone number'
          });
          return false;
        }

        if (!this.formData.country_id) {
          this.errors.add({
            field: 'country_id',
            msg: 'Select country'
          });
          return false;
        }

        if (this.stateList.length && !this.formData.state_id) {
          this.errors.add({
            field: 'state_id',
            msg: 'Select state'
          });
          return false;
        }

        if(valid) cb();
        else return;
      });
    },
    validateDB(field) {
      if (
        !this.formData[field].length || 
        (this.errors.first(field) && 
        !this.errors.first(field).includes(`\`${this.formData[field]}\``))
      )
        return;

      if (
        this.formPrefillData[field] &&
        this.formData[field] == this.formPrefillData[field]
      )
        return;

      this.isValidating[field] = true;
      this.validateAccount({
        [field]: this.formData[field]
      })
        .then(() => {
          this.isValidating[field] = false;
        })
        .catch(e => {
          if (e.response.data.errors) {
            let err = e.response.data.errors;
            let msg = '';

            if (err.base) {
              msg = e.response.data.errors.base[0];
            } else {
              Object.keys(err).forEach(key => {
                msg += `${key.toUpperCase()}: ${err[key][0]}\n`;
              });
            }

            this.errors.add({
              field,
              msg
            });
          }

          this.isValidating[field] = false;
        });
    },
    handleSubmit() {
      this.$emit('submit', this.formData);
    },
    onVerify() {
      this.validateSecondPart(() => {
        const params = {
          email: this.formData.email,
        }
        this.formData.verification_code = '';
        if(this.$route.query && this.$route.query.first_sso === 'true' && this.formPrefillData.email === this.formData.email) 
          this.completeSSOSignUp(this.formData);
        else this.$emit('handleGenerateVerificationCode', params)
      })
    },
    completeSSOSignUp(data) {
      this.showSpinner = true;
      this.updateUser({
          user_name: data.user_name,
          email: data.email,
          additional_email: data.additional_email,
          first_name: data.first_name,
          last_name: data.last_name,
          company_name: data.company_name,
          password: data.password ? data.password : undefined,
          phone: data.phone,
          country_id: data.country_id,
          state_id: data.state_id,
          street: data.street,
          city: data.city,
          zip_code: data.zip_code,           
      })
        .then(res => {
          this.$router.push('/select-plan');
          this.showSpinner = false;
        })
    },
  },
  watch: {
    acceptAll(val) {
      this.formData.accept_terms = val;
      this.formData.accept_policy = val;
    },
    selectedCountry(val) {
      this.formData.country_id = val.id;
    },
    selectedState(val) {
      if (val)
        this.formData.state_id = val.id;
      else
        this.formData.state_id = '';
    },
    formData: {
      deep: true,
      handler() {
        // this.errors.clear();
      }
    },
    backErrors(val) {
      if (val.length) {
        val.forEach(item => {
          this.errors.add(item);
        });

        if (val.some(item => this.firstTabKeys.includes(item.field)))
          this.$emit('tabUpdate', 0);
      }
    },
    provider(val) {
      this.mixinUserAccount = val;
    },
    formPrefillData() {
      this.updateFormData();
    }
  }
}
</script>